import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Users, Target, Award } from 'lucide-react';
import { getToken, clearToken } from '@/lib/auth';
import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function LandingPage() {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then(() => navigate('/dashboard', { replace: true }))
        .catch(() => { clearToken(); setCheckingAuth(false); });
    } else {
      setCheckingAuth(false);
    }
  }, [navigate]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen hero-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hero-bg relative overflow-hidden">
        <div className="noise-texture absolute inset-0 pointer-events-none"></div>
        <div className="relative z-10 text-center px-4 py-16 max-w-6xl mx-auto">
          <div className="text-8xl mb-6">☘️</div>
          <h1 className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white tracking-wider mb-6">
            The Shamrock
          </h1>
          <p className="font-manrope text-xl sm:text-2xl text-emerald-100 mb-12 max-w-2xl mx-auto">
            Your NCAA March Madness bracket pool, Irish style. Pick your teams, track the action, and compete for glory.
          </p>

          {/* How It Works - Also on About page */}
          <div className="mb-12">
            <h2 className="font-bebas text-4xl text-amber-600 mb-8 tracking-wider">
              How It Works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-md border border-white/20">
                <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Target className="text-emerald-900" size={28} />
                </div>
                <h3 className="font-bebas text-xl text-white mb-2 tracking-wide">Pick Teams</h3>
                <p className="font-manrope text-sm text-emerald-100">
                  Select ONE team from each seed (1-16). That's 16 teams total.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-md border border-white/20">
                <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Trophy className="text-emerald-900" size={28} />
                </div>
                <h3 className="font-bebas text-xl text-white mb-2 tracking-wide">Score Points</h3>
                <p className="font-manrope text-sm text-emerald-100">
                  Higher seeds = more points. 16 seed win = 25 pts, down to 1 seed = 10 pts.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-md border border-white/20">
                <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Users className="text-emerald-900" size={28} />
                </div>
                <h3 className="font-bebas text-xl text-white mb-2 tracking-wide">Track Others</h3>
                <p className="font-manrope text-sm text-emerald-100">
                  See what teams your friends picked and compare strategies.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-md border border-white/20">
                <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Award className="text-emerald-900" size={28} />
                </div>
                <h3 className="font-bebas text-xl text-white mb-2 tracking-wide">Win Glory</h3>
                <p className="font-manrope text-sm text-emerald-100">
                  Climb the leaderboard and claim your bragging rights.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              data-testid="google-login-btn"
              onClick={handleGoogleLogin}
              className="btn-secondary px-8 py-4 rounded-md text-xl shadow-lg hover:shadow-xl"
            >
              Sign in with Google
            </button>
            <Link
              to="/register"
              data-testid="register-link"
              className="btn-primary px-8 py-4 rounded-md text-xl shadow-lg hover:shadow-xl"
            >
              Create Account
            </Link>
            <Link
              to="/login"
              data-testid="login-link"
              className="text-white font-manrope text-lg hover:text-amber-600 transition-colors"
            >
              Already have an account?
            </Link>
          </div>
        </div>
      </div>


      {/* Footer */}
      <div className="bg-emerald-950 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="font-manrope">© 2025 The Shamrock Pool. May the luck of the Irish be with you.</p>
        </div>
      </div>
    </div>
  );
}